import { useSyncExternalStore } from 'react';
import type { OpenItem } from '@contracts/ledger';

/**
 * Commitments closed — or reopened — on the page in front of you, kept where
 * they were until you look away.
 *
 * Vladimir, 2026-09-25: *"When i click got it or done on the commitment i
 * want it to stay on its place until reload page or page going out of focus
 * just change its state and cross. Its all to be able to undo easier as right
 * now if i acidentally click done on wrong action it will dissapear and i will
 * need ages to find it in the closed section"*.
 *
 * So a list shows what you acted on in its old place, crossed out, with Undo
 * where Done was. It settles — the closed ones move to Closed recently, and a
 * dropped one goes, because dropped is never listed — when the window loses
 * focus or is hidden, when you go to another page, and on a reload (this is
 * memory, nothing else).
 */

let acted: ReadonlyMap<string, OpenItem> = new Map();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

/** The server's answer to Done, Got it, Drop or Undo — as the row should show it now. */
export function actedOn(item: OpenItem): void {
  acted = new Map(acted).set(item.id, item);
  emit();
}

/** Everything moves to where it belongs. */
export function settle(): void {
  if (acted.size === 0) return;
  acted = new Map();
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('blur', settle);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') settle();
  });
}

export function useActedHere(): ReadonlyMap<string, OpenItem> {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => acted,
  );
}

/**
 * The open list as the reader left it, and the closed list without what is
 * still standing in it.
 *
 * Before the ledger is read again the server's copy is still open: the acted
 * copy stands in for it. After, it is in the closed list: taken back out, in
 * the place the list's own order gives it — every list here orders by fields
 * a close does not change.
 */
export function asLeft(
  open: OpenItem[],
  closed: OpenItem[],
  acted: ReadonlyMap<string, OpenItem>,
): { open: OpenItem[]; closed: OpenItem[] } {
  if (acted.size === 0) return { open, closed };
  const openIds = new Set(open.map((item) => item.id));
  const closedIds = new Set(closed.map((item) => item.id));
  const back = closed.filter((item) => acted.has(item.id) && !openIds.has(item.id));
  // ⚠ A drop is in neither list once the ledger is read again — dropped is
  // never listed, not even as closed (Vladimir, 2026-09-28: "it shall not be
  // shown at all"). The acted copy is all there is, and it keeps the row in
  // its place, with Undo, until the page is left.
  const dropped = [...acted.values()].filter(
    (item) => item.status === 'DROPPED' && !openIds.has(item.id) && !closedIds.has(item.id),
  );
  const merged = [...open, ...back, ...dropped]
    .map((item) => acted.get(item.id) ?? item)
    // The server's own order: newest promise first.
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  return { open: merged, closed: closed.filter((item) => !acted.has(item.id)) };
}
